/**
 * L_ENTITY (profile + directors + secretaries + members) → SG Incorporation (Post) fill values.
 * Prefill only Entity-sourced fields; New/CSP blanks stay for user input.
 */
import { formatRegisteredOfficeAddressFromProfile } from '@/features/startTask/preset_task/sg/components/agm/agm-ufs/composables/agmUfsEntityAutofill';
import { dateToHtmlInput } from '@/features/startTask/preset_task/sg/components/change-financial-year-end/composables/dateToHtmlInput';

function str(v: unknown): string {
  if (v == null) return '';
  return String(v).trim();
}

function pick(...vals: unknown[]): string {
  for (const v of vals) {
    const s = str(v);
    if (s) return s;
  }
  return '';
}

/** CddVarField date 저장과 맞춤 (YYYY-MM-DD) — 화면은 세션 dateFormat 표시 */
function formatDocDate(v: unknown): string {
  return dateToHtmlInput(v) || str(v);
}

function unwrapProfile(raw: any): Record<string, any> {
  let p = raw?.data ?? raw ?? {};
  for (let i = 0; i < 2; i += 1) {
    const inner = p?.data;
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      if (
        inner.registeredAddress != null ||
        inner.regNo != null ||
        inner.REG_NO != null ||
        inner.accountingDates != null
      ) {
        p = inner;
        continue;
      }
    }
    break;
  }
  return p && typeof p === 'object' ? p : {};
}

function personName(row: any): string {
  return pick(
    row?.name,
    row?.contactName,
    row?.fullName,
    `${str(row?.firstName)} ${str(row?.surname || row?.lastName)}`.trim(),
  );
}

function personId(row: any): string {
  return pick(
    row?.contactRegNo,
    row?.CONTACT_REG_NO,
    row?.contact_reg_no,
    row?.idNo,
    row?.identificationNo,
    row?.IDENTIFICATION_NO,
    row?.nric,
    row?.nricNo,
    row?.passportNo,
    row?.ID_NO,
    row?.NRIC_NO,
    row?.PASSPORT_NO,
  );
}

function memberShares(row: any): string {
  return pick(row?.shares, row?.numberOfShares, row?.shareQty, row?.ordinaryShares, row?.qty);
}

function memberSharesQty(row: any): number {
  const n = Number(String(memberShares(row) || '0').replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : 0;
}

function memberCertNo(row: any): string {
  return pick(
    row?.certificateNo,
    row?.certificateNumber,
    row?.certiNo,
    row?.certNo,
    row?.CERTI_NO,
    row?.CERT_NO,
    row?.shareCertificateNo,
    row?.share_certificate_number,
  );
}

/** Cert 번호 오름차순 (숫자 우선, 없으면 문자열, 빈 값은 맨 뒤) */
function certSortKey(cert: string): { empty: number; num: number; text: string } {
  const s = String(cert || '').trim();
  if (!s) return { empty: 1, num: Number.POSITIVE_INFINITY, text: '' };
  const n = Number(s.replace(/,/g, ''));
  if (Number.isFinite(n)) return { empty: 0, num: n, text: s.toLowerCase() };
  return { empty: 0, num: Number.POSITIVE_INFINITY, text: s.toLowerCase() };
}

function compareByCertAsc(a: any, b: any): number {
  const ka = certSortKey(memberCertNo(a));
  const kb = certSortKey(memberCertNo(b));
  if (ka.empty !== kb.empty) return ka.empty - kb.empty;
  if (ka.num !== kb.num) return ka.num - kb.num;
  return ka.text.localeCompare(kb.text);
}

function appointmentDate(row: any): string {
  return pick(row?.appointmentDate, row?.dateOfAppointment, row?.APPOINTMENT_DATE);
}

export interface SgIncPostLocalState {
  shareIndIds: number[];
  shareCorpIds: number[];
  ddfIds: number[];
  includeNominee: boolean;
  includePaidUp?: boolean;
  firstResDirectorIds: number[];
  firstResAllotteeIds: number[];
  indemnityIds: number[];
  welcomeContactIds: number[];
}

export interface SgIncPostMapped {
  values: Record<string, string>;
  local: SgIncPostLocalState;
}

/**
 * Map L_ENTITY snapshot → Post document field values + N부 local state.
 */
export function mapSgEntityToPostValues(input: {
  profile?: any;
  entityHint?: Record<string, any> | null;
  directors?: any[];
  secretaries?: any[];
  members?: any[];
  cspName?: string;
}): SgIncPostMapped {
  const values: Record<string, string> = {};
  const set = (k: string, v: unknown) => {
    const s = str(v);
    if (s) values[k] = s;
  };

  const profile = unwrapProfile(input.profile);
  const hint = input.entityHint || {};
  const accounting = (profile.accountingDates as Record<string, any>) || {};

  const companyName = pick(
    hint.companyName,
    hint.entityName,
    profile.companyName,
    profile.entityName,
    profile.COMPANY_NAME,
  );
  const regNo = pick(
    hint.registrationNo,
    hint.regNo,
    profile.regNo,
    profile.REG_NO,
    profile.registrationNo,
    profile.uen,
    profile.UEN,
  );
  const incorpDate = formatDocDate(
    pick(
      hint.incorporationDate,
      hint.dateOfIncorporation,
      accounting.incorporationDate,
      profile.incorporationDate,
      profile.INCORPORATION_DATE,
      profile.dateOfIncorporation,
      profile.DATE_OF_INCORPORATION,
    ),
  );
  const office = pick(
    hint.registeredOfficeAddress,
    hint.registeredAddress,
    formatRegisteredOfficeAddressFromProfile(profile),
  );
  const fyeRaw = pick(
    accounting.financialYearEnd,
    profile.financialYearEnd,
    profile.FINANCIAL_YEAR_END,
  );
  const fye = formatDocDate(fyeRaw) || fyeRaw;

  set('company_name', companyName);
  set('company_registration_no', regNo);
  set('company_uen', regNo);
  set('company_registered_address', office);
  set('registered_office_address', office);
  set('date_of_incorporation', incorpDate);
  set('financial_year_end', fye);
  set('first_financial_year_end', fye);
  set('subsequent_financial_year_end', fye);
  set('currency_symbol', 'S$');
  set('resolution_date', incorpDate);

  if (input.cspName) {
    set('csp_name', input.cspName);
  }

  const directors = (input.directors || []).filter((d) => personName(d));
  const secretaries = (input.secretaries || []).filter((s) => personName(s));
  // 해임/잔여주식 0 주주 제외 → cert 오름차순
  const members = (input.members || [])
    .filter((m) => personName(m) && memberSharesQty(m) > 0)
    .slice()
    .sort(compareByCertAsc);

  const firstDir = directors[0];
  if (firstDir) {
    // Form A1 declarant / Nomination director 등 「이사 1인」필드는 수동 지정
    // (DDF 등은 ddf_N/director_name 스코프로 채움)
    set('company_signatory_name', personName(firstDir));
    set('company_signatory_title', 'Director');
    set('signatory_name', personName(firstDir));
    set('declarant_name', personName(firstDir));
    set('declarant_identification_no', personId(firstDir));
  }

  const firstSec = secretaries[0];
  if (firstSec) {
    set('secretary_name', personName(firstSec));
  }

  const firstResDirectorIds: number[] = [];
  directors.forEach((d, i) => {
    const id = i + 1;
    firstResDirectorIds.push(id);
    set(`dir_${id}/d_name`, personName(d));
  });
  if (!firstResDirectorIds.length) firstResDirectorIds.push(1);

  const indemnityIds: number[] = [];
  directors.forEach((d, i) => {
    const id = i + 1;
    indemnityIds.push(id);
    set(`indem_${id}/d_name`, personName(d));
    set(`indem_${id}/d_identification_no`, personId(d));
  });
  if (!indemnityIds.length) indemnityIds.push(1);

  const ddfIds: number[] = [];
  directors.forEach((d, i) => {
    const id = i + 1;
    ddfIds.push(id);
    set(`ddf_${id}/company_name`, companyName);
    set(`ddf_${id}/company_registration_no`, regNo);
    set(`ddf_${id}/director_name`, personName(d));
    set(`ddf_${id}/director_identification_no`, personId(d));
    set(
      `ddf_${id}/appointment_date`,
      formatDocDate(appointmentDate(d)) || appointmentDate(d),
    );
  });
  if (!ddfIds.length) ddfIds.push(1);

  const firstResAllotteeIds: number[] = [];
  let allotSeq = 0;

  // First Directors Resolution — Entity 멤버(잔여주식>0)를 allottee 로 반영 (cert 오름차순)
  members.forEach((m) => {
    const name = personName(m);
    const shares = memberShares(m) || '';
    const certNo = memberCertNo(m);
    allotSeq += 1;
    firstResAllotteeIds.push(allotSeq);
    set(`allot_${allotSeq}/a_name`, name);
    set(`allot_${allotSeq}/a_number_of_shares`, shares);
    set(`allot_${allotSeq}/a_share_certificate_number`, certNo);
  });

  if (!firstResAllotteeIds.length) firstResAllotteeIds.push(1);

  // Share Application (Ind/Corp) — 기본 0부. Stage 2 에서 추가해 사용.
  // 발행회사(company_*) 는 추가 시 PackageDoc 이 루트 값으로 시드.
  const shareIndIds: number[] = [];
  const shareCorpIds: number[] = [];

  const welcomeContactIds = [1];

  return {
    values,
    local: {
      shareIndIds,
      shareCorpIds,
      ddfIds,
      includeNominee: false,
      includePaidUp: false,
      firstResDirectorIds,
      firstResAllotteeIds,
      indemnityIds,
      welcomeContactIds,
    },
  };
}
